import { Link } from 'react-router-dom';
import { ArrowRight, Globe, MapPin, Building2, Users, Clock, Shield } from 'lucide-react';
import PageHeader from '../components/layout/PageHeader';

const HEADER_BG = 'https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eSUyMGJ1c2luZXNzfGVufDB8fHx8MTc3MjQ0MDI1Nnww&ixlib=rb-4.1.0&q=85';
const GLOBAL_IMG = 'https://images.unsplash.com/photo-1739300293396-9ad79111c8e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB0ZWFtJTIwdGVjaG5vbG9neSUyMHdvcmtwbGFjZXxlbnwwfHx8fDE3NzIxMDI4Mzh8MA&ixlib=rb-4.1.0&q=85';

const regions = [
  { icon: MapPin, title: 'Canada (Headquarters)', desc: 'Headquartered in Ontario with comprehensive staffing solutions across Toronto, Vancouver, Montreal, Ottawa, Calgary, and all major Canadian markets.' },
  { icon: MapPin, title: 'United States', desc: 'Coast-to-coast coverage with deep networks across major tech hubs including Silicon Valley, New York, Austin, Seattle, and Chicago.' },
  { icon: MapPin, title: 'United Kingdom', desc: 'Strategic talent acquisition across London, Manchester, Edinburgh, and other key technology centres.' },
];

const capabilities = [
  { icon: Building2, title: 'Enterprise Clients', desc: 'Serving Fortune 500 companies and mid-market enterprises with scalable staffing solutions.' },
  { icon: Users, title: 'Cross-Border Teams', desc: 'Building distributed teams across time zones with seamless coordination and compliance.' },
  { icon: Clock, title: 'Rapid Deployment', desc: 'Average 72-hour candidate submission with accelerated onboarding processes.' },
  { icon: Shield, title: 'Compliance Assured', desc: 'Full regulatory compliance across all operating jurisdictions including work permits and tax requirements.' },
  { icon: Globe, title: 'Market Intelligence', desc: 'Real-time salary benchmarking and talent market insights across all three countries.' },
  { icon: Users, title: 'Dedicated Teams', desc: 'Assigned account managers with regional expertise for each client engagement.' },
];

const GlobalStaffing = () => {
  return (
    <div data-testid="global-staffing-page">
      <PageHeader caption="Services" title="Global Staffing" subtitle="Strategic workforce solutions across North America and the United Kingdom." bgImage={HEADER_BG} />

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="sol-caption mb-4">Our Global Reach</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Talent Without Borders</h2>
              <p className="text-[#525252] leading-relaxed mb-4">
                In today's interconnected economy, accessing the right talent shouldn't be limited by geography. Headquartered in Canada, Solnity's global staffing practice connects enterprise clients with pre-vetted IT professionals across Canada, the United States, and the United Kingdom.
              </p>
              <p className="text-[#525252] leading-relaxed mb-8">Our regional experts understand local market dynamics, regulatory requirements, and cultural nuances, ensuring seamless talent acquisition regardless of location.</p>
              <Link to="/hire-talent" className="sol-btn-primary text-sm">Start Hiring <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="sol-img-overlay aspect-[4/3]">
              <img src={GLOBAL_IMG} alt="Global team collaboration" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Coverage</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Operating Regions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {regions.map((r) => (
              <div key={r.title} className="sol-service-card bg-white">
                <r.icon className="w-6 h-6 mb-5 text-black" strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{r.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white sol-section">
        <div className="sol-container">
          <div className="text-center mb-16">
            <p className="sol-caption mb-4">Capabilities</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>What Sets Us Apart</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c) => (
              <div key={c.title} className="sol-service-card">
                <c.icon className="w-6 h-6 mb-4 text-black" strokeWidth={1.5} />
                <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{c.title}</h3>
                <p className="text-sm text-[#525252] leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sol-cta-strip">
        <div className="sol-container text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Expand Your Team Globally</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">Connect with our global staffing experts to find the right talent for your organization.</p>
          <Link to="/hire-talent" data-testid="global-cta-hire" className="sol-btn-inverse text-sm">Hire Talent <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </div>
  );
};

export default GlobalStaffing;
